import React, { useState, useEffect } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { python } from "@codemirror/lang-python";
import { javascript } from "@codemirror/lang-javascript";
import { cpp } from "@codemirror/lang-cpp";
import { java } from "@codemirror/lang-java";
import { dracula } from "@uiw/codemirror-theme-dracula";
import { useLocation } from "react-router-dom";
import "./Code.css";

const CodeEditor = () => {
  const location = useLocation();
  const { question: initialQuestion } = location.state || {};
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [language, setLanguage] = useState("python");
  const [code, setCode] = useState("");
  const [output, setOutput] = useState("");
  const [analysis, setAnalysis] = useState("");
  const [cc, setcc] = useState("");
  const [testResults, setTestResults] = useState([]);
  const [testSummary, setTestSummary] = useState({ passed: 0, total: 0 });
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [userResponses, setUserResponses] = useState([]);
  const [showSubmit, setShowSubmit] = useState(false);

  const fetchQuestionsIfNeeded = async () => {
    if (!initialQuestion || Object.keys(initialQuestion).length === 0) {
      try {
        const response = await fetch("/api/get_coding_questions");
        const data = await response.json();
        console.log("Fetched question data:", data);

        const extractedQuestions = Array.isArray(data.question_data)
          ? data.question_data.flat()
          : [];

        if (extractedQuestions.length > 0) {
          setQuestions(extractedQuestions);
          setCode(extractedQuestions[0]?.boilerplate?.[language] || "");
          // Initialize user responses array
          setUserResponses(Array(extractedQuestions.length).fill(""));
        } else {
          console.error("Unexpected or empty question format:", data);
        }
      } catch (error) {
        console.error("Failed to fetch questions:", error.message);
      }
    }
  };

  useEffect(() => {
    fetchQuestionsIfNeeded();
  }, []);

  useEffect(() => {
    if (questions.length > 0) {
      const current = questions[currentIndex];
      setCode(current?.boilerplate?.[language] || "");
      // Update showSubmit based on current index
      setShowSubmit(currentIndex === questions.length - 1);
    }
  }, [language, currentIndex, questions]);

  const handleRun = async () => {
    setOutput("Running code...");
    setTestResults([]);
    setTestSummary({ passed: 0, total: 0 });

    try {
      const response = await fetch("/api/run_code", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          question: questions[currentIndex],
          questionId: currentIndex,
          code,
          language,
          test_limit: 2 // Run only first 2 test cases
        }),
      });

      const data = await response.json();

      if (data.error) {
        setOutput(`Error: ${data.error}`);
      } else {
        setTestResults(data.results);
        const passedCount = data.results.filter((t) => t.passed).length;
        setTestSummary({ passed: passedCount, total: data.results.length });
        setOutput("Execution complete.");
        
        // Update user responses
        const updatedResponses = [...userResponses];
        updatedResponses[currentIndex] = code;
        setUserResponses(updatedResponses);
      }
    } catch (error) {
      setOutput(`Server error: ${error.message}`);
    }
  };

  const handleAnalyze = async () => {
    setAnalysis("Analyzing code...");
    try {
      const response = await fetch("/api/analyze_code", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          question: questions[currentIndex],
          user_response: code,
          language 
        }),
      });

      const data = await response.json();
      if (data.error) {
        setAnalysis(`Error: ${data.error}`);
      } else {
        setAnalysis(data.explanation);
        setcc(data.corrected_code);
      }
    } catch (error) {
      setAnalysis(`Server error: ${error.message}`);
    }
  };

  const handleNextQuestion = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handleSubmitAll = async () => {
    try {
      // Update user responses
      const updatedResponses = [...userResponses];
      updatedResponses[currentIndex] = code;
      setUserResponses(updatedResponses);
      const response = await fetch("/api/submit_code", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          test_id: new URLSearchParams(location.search).get("test_id"),
          questions,
          user_responses: updatedResponses,
          language
        }),
      });

      const data = await response.json();
      if (data.error) {
        alert(`Submission error: ${data.error}`);
      } else {
        alert("All responses submitted successfully!");
        // Redirect or show results
      }
    } catch (error) {
      alert(`Submission failed: ${error.message}`);
    }
  };

  const getLanguageExtension = () => {
    switch (language) {
      case "python":
        return python();
      case "javascript":
        return javascript();
      case "cpp":
        return cpp();
      case "java":
        return java();
      default:
        return python();
    }
  };

  const toggleFullScreen = () => {
    const elem = document.querySelector(".code-container");
    if (!isFullScreen) {
      elem?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
    setIsFullScreen(!isFullScreen);
  };

  const currentQuestion = questions[currentIndex];

  return (
    <div className="code-container">
      {/* Left Panel - Question Section */}
      <div className="question-section">
        <div className="question-header">
          <h2 className="question-title">{currentQuestion?.title || "Problem Title"}</h2>
          {showSubmit ? (
            <button className="submit-all-button" onClick={handleSubmitAll}>
              Submit All
            </button>
          ) : (
            <button className="next-question-button" onClick={handleNextQuestion}>
              Next Question
            </button>
          )}
        </div>
        
        <div className="question-content">
          <p className="question-text">{currentQuestion?.description || "Problem description goes here..."}</p>

          <h3 className="test-cases-heading">Test Cases</h3>
          <ul className="test-cases">
            {currentQuestion?.test_cases?.map((test, index) => (
              <li key={index} className="test-case">
                <strong>Input:</strong> {JSON.stringify(test.input)}
                <br />
                <strong>Expected:</strong> {JSON.stringify(test.expected)}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Right Panel - Code Editor */}
      <div className="editor-section">
        <div className="editor-header">
          <select
            className="language-select"
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
          >
            <option value="python">Python</option>
            <option value="javascript">JavaScript</option>
            <option value="cpp">C++</option>
            <option value="java">Java</option>
          </select>
          
          <div className="button-group">
            <button className="run-button" onClick={handleRun}>
              Run Code
            </button>
            <button className="analyze-button" onClick={handleAnalyze}>
              Analyze Code
            </button>
            <button className="fullscreen-button" onClick={toggleFullScreen}>
              {isFullScreen ? "Exit Full Screen" : "Full Screen"}
            </button>
          </div>
        </div>

        <CodeMirror
          value={code}
          height="400px"
          theme={dracula}
          extensions={[getLanguageExtension()]}
          onChange={(value) => setCode(value)}
          className="code-mirror"
        />

        {/* Test Results Section */}
        <div className="test-results-container">
          <h3>Test Results</h3>
          {testSummary.total > 0 && (
            <p className="test-summary">
              Passed: {testSummary.passed}/{testSummary.total}{" "}
              {testSummary.passed === testSummary.total ? "✅" : "❌"}
            </p>
          )}
          <ul className="test-results-list">
            {testResults.map((test, index) => (
              <li key={index} className={test.passed ? "test-passed" : "test-failed"}>
                <strong>Test {index + 1}:</strong> {test.passed ? "✅ Passed" : "❌ Failed"}
                <br />
                <strong>Input:</strong> {test.input}
                <br />
                <strong>Expected:</strong> {test.expected}
                <br />
                <strong>Output:</strong> {test.output}
                {test.error && (
                  <>
                    <br />
                    <strong>Error:</strong> <span className="error-message">{test.error}</span>
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>

        {/* Code Analysis Section */}
        <div className="analysis-container">
          <h3>Code Analysis</h3>
          {analysis && <pre className="analysis-box">{analysis}</pre>}
          {cc && <pre className="corrected-code">{cc}</pre>}
        </div>
      </div>
    </div>
  );
};

export default CodeEditor;